import { computed, inject, Signal } from '@angular/core';
import { AssetAnalysisItem } from './api';
import { FeatureFlagStore } from '../stores/feature-flags.store';
import { v3ComponentContext } from './v3-component-context';
import { findV3AnalysisSignal } from './v3-guest-mode';
import { updatedAtSignal } from '../analysis-data';
import {
  NEXT_EARNINGS_DATE,
  EarningsStats,
  EarningsStatDataSet,
  deriveEpsData,
  deriveRevenueStats,
  deriveQuarterSurprise,
  deriveNextEarningsReportLabel,
  epsCurrencySymbol,
  formatFiscalPeriod,
} from '../earnings-data';

export interface EarningsAnalysisData {
  isFinnet: Signal<boolean>;
  isDesktop: Signal<boolean>;
  earningsAnalysis: Signal<AssetAnalysisItem | null>;
  earningsTranscripts: Signal<AssetAnalysisItem | null>;
  updatedAt: Signal<Date | null>;
  title: Signal<string>;
  epsData: Signal<EarningsStatDataSet | null>;
  epsSymbol: Signal<string>;
  fiscalPeriod: Signal<string>;
  quarterSurprisePercent: Signal<number | null>;
  quarterSurpriseBeat: Signal<boolean>;
  revenueStats: Signal<EarningsStats | null>;
  nextEarningsDate: string;
  // zero_feature-ai-earnings-date — the hardcoded date stays hidden until the backend provides it.
  isEarningsDateEnabled: Signal<boolean>;
  nextEarningsReportLabel: Signal<string>;
}

// Earnings lookups and computeds shared by V3EarningsOverview, V3EarningsAnalysis and
// V3EarningsDesktopContent. Caller-specific bits (the sub-page's updates/FAQ/texts, the card's
// Finnet report-period subtitle) stay local.
export function earningsAnalysisData(): EarningsAnalysisData {
  const ctx = v3ComponentContext();
  const featureFlagStore = inject(FeatureFlagStore);

  const earningsAnalysis = findV3AnalysisSignal(
    ctx.assetsStore.v2Data,
    'earnings_report',
    ctx.isGuestMode,
  );
  const earningsTranscripts = findV3AnalysisSignal(
    ctx.assetsStore.v2Data,
    'earnings_transcripts',
    ctx.isGuestMode,
  );

  const content = computed(() => earningsAnalysis()?.content);
  const epsData = computed(() => deriveEpsData(content()));
  const quarterSurprise = computed(() => deriveQuarterSurprise(epsData()));
  const revenueStats = computed(() => deriveRevenueStats(content()));

  return {
    isFinnet: ctx.layout.isFinnet,
    isDesktop: ctx.layout.isDesktop,
    earningsAnalysis,
    earningsTranscripts,
    updatedAt: updatedAtSignal(earningsAnalysis),
    // Highlight title — the field V2's EarningsOverview reads as `subtitle`.
    title: computed(() => earningsAnalysis()?.title ?? ''),
    epsData,
    // Same helper EpsChart uses internally, so the symbol always matches the chart's own data.
    epsSymbol: computed(() => epsCurrencySymbol(epsData())),
    fiscalPeriod: computed(() => formatFiscalPeriod(content())),
    quarterSurprisePercent: computed(() => quarterSurprise()?.percent ?? null),
    quarterSurpriseBeat: computed(() => quarterSurprise()?.beat ?? true),
    revenueStats,
    nextEarningsDate: NEXT_EARNINGS_DATE,
    isEarningsDateEnabled: featureFlagStore.isEarningsDateEnabled,
    nextEarningsReportLabel: computed(() => deriveNextEarningsReportLabel(epsData())),
  };
}
